/**
 * WorldViewerPage - 360° virtual tour for a specific world
 *
 * Features:
 * - Photo Sphere Viewer with virtual tour navigation
 * - Private Comments sidebar (saved to Supabase, viewable in admin)
 * - Analytics tracking per room viewed
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Viewer } from '@photo-sphere-viewer/core'
import { VirtualTourPlugin } from '@photo-sphere-viewer/virtual-tour-plugin'
import '@photo-sphere-viewer/core/index.css'
import '@photo-sphere-viewer/virtual-tour-plugin/index.css'
import type { World } from '@/config/worldsConfig'
import { BRAND_COLORS } from '@/config/brandConfig'
import { useAuth } from '@/features/auth'
import { trackWorldsEvent } from '@/services/worldsAnalytics'
import { supabase } from '@/services/supabase'
import { fetchWorlds } from '@/services/worldsService'

interface Comment {
  id: string
  world_id: string
  user_id: string | null
  user_name: string
  text: string
  created_at: string
}

async function fetchComments(worldId: string): Promise<Comment[]> {
  const { data, error } = await supabase
    .from('worlds_comments')
    .select('*')
    .eq('world_id', worldId)
    .order('created_at', { ascending: true })
    .limit(200)
  if (error) {
    console.warn('[Comments] Failed to fetch:', error.message)
    return []
  }
  return (data || []) as Comment[]
}

async function insertComment(comment: Omit<Comment, 'id' | 'created_at'>): Promise<Comment | null> {
  const { data, error } = await supabase
    .from('worlds_comments')
    .insert(comment)
    .select()
    .single()
  if (error) {
    console.warn('[Comments] Failed to insert:', error.message)
    return null
  }
  return data as Comment
}

// Private Comments Sidebar
function CommentsSidebar({
  worldId,
  isOpen,
  onClose,
}: {
  worldId: string
  isOpen: boolean
  onClose: () => void
}) {
  const { user } = useAuth()
  const [comments, setComments] = useState<Comment[]>([])
  const [text, setText] = useState('')
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    fetchComments(worldId).then(setComments)
  }, [worldId])

  const handleSend = useCallback(async () => {
    if (!text.trim()) return
    const row = {
      world_id: worldId,
      user_id: user?.id ?? null,
      user_name: user?.firstName || user?.username || user?.email || 'Guest',
      text: text.trim(),
    }
    setText('')
    const inserted = await insertComment(row)
    if (inserted) {
      setComments((prev) => [...prev, inserted])
    }
    trackWorldsEvent('world_comment_added', { world_id: worldId })
    setTimeout(() => listRef.current?.scrollTo(0, listRef.current.scrollHeight), 50)
  }, [text, worldId, user])

  if (!isOpen) return null

  return (
    <div className="absolute right-0 top-0 bottom-0 w-full sm:w-80 bg-gray-950/95 backdrop-blur-lg border-l border-gray-800 flex flex-col z-30 animate-slide-in-right">
      {/* Header */}
      <div className="p-4 border-b border-gray-800 flex items-center justify-between shrink-0">
        <span className="text-white text-sm font-semibold">Private Comments</span>
        <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors text-lg leading-none">&times;</button>
      </div>

      {/* Messages */}
      <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {comments.length === 0 && (
          <p className="text-gray-600 text-sm text-center mt-8">No comments yet. Be the first!</p>
        )}
        {comments.map((c) => (
          <div key={c.id} className="bg-gray-900 rounded-lg p-3">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-white text-xs font-medium">{c.user_name}</span>
              <span className="text-gray-600 text-[10px]">
                {new Date(c.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">{c.text}</p>
          </div>
        ))}
      </div>

      {/* Input */}
      <div className="p-3 border-t border-gray-800 shrink-0">
        <div className="flex gap-2">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Leave a comment..."
            className="flex-1 bg-gray-900 text-white text-sm rounded-lg px-3 py-2 border border-gray-800 focus:border-pink-500/50 focus:outline-none placeholder-gray-600"
          />
          <button
            onClick={handleSend}
            disabled={!text.trim()}
            className="px-3 py-2 rounded-lg text-sm font-medium text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:opacity-90"
            style={{ backgroundColor: BRAND_COLORS.primary }}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  )
}

export function WorldViewerPage() {
  const navigate = useNavigate()
  const { worldId } = useParams<{ worldId: string }>()
  const containerRef = useRef<HTMLDivElement>(null)
  const viewerRef = useRef<Viewer | null>(null)
  const [currentRoom, setCurrentRoom] = useState('')
  const [showComments, setShowComments] = useState(false)
  const [error, setError] = useState('')
  const [startTime] = useState(Date.now())
  const [world, setWorld] = useState<World | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchWorlds().then((worlds) => {
      const found = worlds.find((w) => w.id === worldId)
      setWorld(found || null)
      setLoading(false)
    })
  }, [worldId])

  useEffect(() => {
    if (!containerRef.current || !world || loading) return

    const psvNodes = world.nodes.map((node) => ({
      id: node.id,
      name: node.name,
      panorama: node.panorama,
      links: node.links,
    }))

    let viewer: Viewer | null = null

    try {
      viewer = new Viewer({
        container: containerRef.current,
        loadingTxt: 'Loading...',
        touchmoveTwoFingers: false,
        mousewheelCtrlKey: false,
        navbar: false,
        plugins: [
          [VirtualTourPlugin, {
            dataMode: 'client',
            positionMode: 'manual',
            renderMode: '3d',
            nodes: psvNodes,
            startNodeId: world.startNodeId,
            preload: true,
            // Tighter overlap angle so close arrows don't get hidden
            arrowsPosition: {
              minPitch: 0.15,
              maxPitch: Math.PI / 2.5,
              linkOverlapAngle: Math.PI / 12, // 15° instead of default 45° — show even close arrows
            },
            transitionOptions: {
              showLoader: true,
              speed: '20rpm',
              effect: 'fade',
              rotation: true,
            },
          } as Record<string, unknown>],
        ],
      })

      viewerRef.current = viewer

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const vt = viewer.getPlugin(VirtualTourPlugin) as any
      if (vt) {
        vt.addEventListener('node-changed', (e: { node?: { id: string; name: string } }) => {
          if (e.node) {
            setCurrentRoom(e.node.name || e.node.id)
            trackWorldsEvent('world_room_viewed', { world_id: world.id, room_id: e.node.id })
          }
        })
      }

      const startNode = world.nodes.find((n) => n.id === world.startNodeId)
      if (startNode) setCurrentRoom(startNode.name)

      trackWorldsEvent('world_tour_started', { world_id: world.id })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load')
    }

    return () => {
      const duration = Math.round((Date.now() - startTime) / 1000)
      trackWorldsEvent('world_tour_ended', { world_id: world?.id, duration_seconds: duration })
      if (viewerRef.current) {
        viewerRef.current.destroy()
        viewerRef.current = null
      }
    }
  }, [world, startTime, loading])

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500" />
      </div>
    )
  }

  if (!world) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-lg mb-4">World not found</p>
          <button onClick={() => navigate('/worlds')} className="text-pink-400 hover:underline text-sm">
            Back to Worlds
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen bg-black flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-black/80 backdrop-blur-lg border-b border-gray-800/50 px-4 py-3 flex items-center justify-between shrink-0 z-20">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/worlds')}
            className="text-gray-400 hover:text-white text-sm transition-colors"
          >
            &larr;
          </button>
          <h1 className="text-white text-sm font-semibold">{world.title}</h1>
          {currentRoom && (
            <span style={{ color: BRAND_COLORS.primary }} className="text-xs font-medium">
              {currentRoom}
            </span>
          )}
        </div>
        <button
          onClick={() => setShowComments(!showComments)}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            showComments
              ? 'bg-pink-500/20 text-pink-400'
              : 'bg-gray-800 text-gray-400 hover:text-white'
          }`}
        >
          Private Comments
        </button>
      </div>

      {/* Viewer + sidebar */}
      <div className="flex-1 relative">
        <div ref={containerRef} style={{ width: '100%', height: '100%', position: 'absolute', inset: 0 }} />

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black z-30">
            <div className="bg-gray-900 rounded-2xl px-8 py-6 text-center max-w-md border border-red-500/30">
              <p className="text-red-400 font-semibold mb-2">Failed to load tour</p>
              <p className="text-gray-400 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Controls hint */}
        {!error && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
            <div className="bg-black/70 backdrop-blur rounded-lg px-5 py-2 text-center">
              <p className="text-gray-400 text-xs">
                Drag to look around &middot; Click arrows to move &middot; Scroll to zoom
              </p>
            </div>
          </div>
        )}

        {/* Private Comments sidebar */}
        <CommentsSidebar
          worldId={world.id}
          isOpen={showComments}
          onClose={() => setShowComments(false)}
        />
      </div>
    </div>
  )
}
